import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useMetaConversionsLayout } from '@/layouts/MetaConversionsLayout'
import { formatCurrency, formatDateTime, formatNumber } from '@/lib/campaign-dashboard'
import {
  fetchMetaConversionsEventDetail,
  fetchMetaConversionsEvents,
} from '@/lib/meta-conversions'
import { fetchGa4ConversionsEventDetail, fetchGa4ConversionsEvents } from '@/lib/google-conversions'
import type { Ga4ConversionsEventDetailPayload } from '@/types/google-conversions'
import type {
  MetaConversionsEventDetailPayload,
  MetaConversionsEventListItem,
  MetaConversionsEventStatus,
  MetaConversionsPagination,
} from '@/types/meta-conversions'
import { buildDefaultMetaDateRange } from './dateRange'
import type { MetaConversionsDashboardChannel } from './MetaConversionsDashboardPage'
import styles from './MetaConversionsPage.module.css'

const PER_PAGE = 20

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'sent':
      return `${styles.statusBadge} ${styles.statusSent}`
    case 'failed':
      return `${styles.statusBadge} ${styles.statusFailed}`
    case 'skipped':
      return `${styles.statusBadge} ${styles.statusSkipped}`
    default:
      return styles.statusBadge
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'sent':
      return 'Enviado'
    case 'failed':
      return 'Falhou'
    case 'skipped':
      return 'Ignorado'
    default:
      return status
  }
}

function stringifyJson(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2)
}

interface EventDetailModalProps {
  campaignId: number
  eventId: number
  channel: MetaConversionsDashboardChannel
  onClose: () => void
}

function EventDetailModal({ campaignId, eventId, channel, onClose }: EventDetailModalProps) {
  const { token } = useAuth()
  const [detail, setDetail] = useState<
    MetaConversionsEventDetailPayload | Ga4ConversionsEventDetailPayload | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const customDataHeading =
    channel === 'meta' ? 'Custom data enviado à Meta' : 'Parâmetros gravados para o Measurement Protocol'

  const downstreamResponseHeading =
    channel === 'meta' ? 'Resposta da Meta' : 'Resposta do Google Analytics'

  const modalTitle =
    channel === 'meta' ? 'Detalhes do evento Meta CAPI' : 'Detalhes do envio GA4 Measurement Protocol'

  const modalSubtitle =
    channel === 'meta'
      ? 'Auditoria do payload enviado, resposta HTTP da Meta e contexto de tracking.'
      : 'Auditoria do payload enviado ao Google, resposta HTTP e contexto de tracking do PIX.'

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        if (channel === 'meta') {
          const response = await fetchMetaConversionsEventDetail(
            campaignId,
            eventId,
            token ?? undefined
          )
          if (!cancelled) {
            setDetail(response)
          }
        } else {
          const response = await fetchGa4ConversionsEventDetail(
            campaignId,
            eventId,
            token ?? undefined
          )
          if (!cancelled) {
            setDetail(response)
          }
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null)
          setError(
            err instanceof Error ? err.message : 'Não foi possível carregar os detalhes do evento.'
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [campaignId, eventId, token, channel])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="conversions-event-modal-title"
      onClick={onClose}
    >
      <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleBlock}>
            <h2 id="conversions-event-modal-title" className={styles.modalTitle}>
              {modalTitle}
            </h2>
            <p className={styles.modalSubtitle}>{modalSubtitle}</p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar detalhes do evento"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.centeredState}>
              <Icon icon="mdi:loading" className={styles.spinner} />
              Carregando detalhes...
            </div>
          ) : error || !detail ? (
            <div className={styles.centeredState}>
              <Icon icon="mdi:alert-circle-outline" className={styles.stateIcon} />
              {error ?? 'Evento não encontrado.'}
            </div>
          ) : (
            <>
              {detail.event.history_partial ? (
                <p className={styles.warningNote}>
                  Este evento foi reconstruído com histórico parcial. Tentativas antigas podem não ter
                  payload salvo.
                </p>
              ) : null}

              <div className={styles.detailGrid}>
                <section className={styles.detailCard}>
                  <h3 className={styles.detailTitle}>Resumo do evento</h3>
                  <div className={styles.detailList}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>ID do evento</span>
                      <span className={styles.detailValue}>{detail.event.event_id}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Tipo</span>
                      <span className={styles.detailValue}>{detail.event.event_name}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Status</span>
                      <span className={styles.detailValue}>{statusLabel(detail.event.status)}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Evento em</span>
                      <span className={styles.detailValue}>{formatDateTime(detail.event.event_time)}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Enviado em</span>
                      <span className={styles.detailValue}>{formatDateTime(detail.event.sent_at)}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Tentativas</span>
                      <span className={styles.detailValue}>{formatNumber(detail.event.attempts_count)}</span>
                    </div>
                  </div>
                </section>

                <section className={styles.detailCard}>
                  <h3 className={styles.detailTitle}>Compra e comprador</h3>
                  <div className={styles.detailList}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Campanha</span>
                      <span className={styles.detailValue}>{detail.campaign.title ?? '—'}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Pagamento</span>
                      <span className={styles.detailValue}>
                        {detail.payment.id ? `#${detail.payment.id}` : '—'}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Valor</span>
                      <span className={styles.detailValue}>
                        {detail.payment.amount != null ? formatCurrency(detail.payment.amount) : '—'}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Comprador</span>
                      <span className={styles.detailValue}>{detail.donor.name ?? '—'}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Email</span>
                      <span className={styles.detailValue}>{detail.donor.email ?? '—'}</span>
                    </div>
                  </div>
                </section>

                <section className={styles.detailCard}>
                  <h3 className={styles.detailTitle}>Tracking e atribuição</h3>
                  <div className={styles.detailList}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>Origem do checkout</span>
                      <span className={styles.detailValue}>{detail.event.checkout_origin ?? '—'}</span>
                    </div>
                    {channel === 'meta' ? (
                      <div className={styles.detailRow}>
                        <span className={styles.detailKey}>Origem da ação Meta</span>
                        <span className={styles.detailValue}>
                          {(detail as MetaConversionsEventDetailPayload).event.action_source ?? '—'}
                        </span>
                      </div>
                    ) : (
                      <div className={styles.detailRow}>
                        <span className={styles.detailKey}>Client ID GA4</span>
                        <span className={styles.detailValue}>
                          {(detail as Ga4ConversionsEventDetailPayload).event.client_id ?? '—'}
                        </span>
                      </div>
                    )}
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>UTM source</span>
                      <span className={styles.detailValue}>{detail.tracking.utm.source ?? '—'}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>UTM campaign</span>
                      <span className={styles.detailValue}>{detail.tracking.utm.campaign ?? '—'}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailKey}>URL de origem do evento</span>
                      <span className={styles.detailValue}>{detail.event.event_source_url ?? '—'}</span>
                    </div>
                  </div>
                </section>
              </div>

              <section className={styles.detailCard}>
                <h3 className={styles.detailTitle}>Snapshot completo de rastreamento</h3>
                <pre className={styles.jsonBlock}>{stringifyJson(detail.tracking.tracking_snapshot)}</pre>
              </section>

              <section className={styles.detailCard}>
                <h3 className={styles.detailTitle}>{customDataHeading}</h3>
                <pre className={styles.jsonBlock}>{stringifyJson(detail.custom_data)}</pre>
              </section>

              <section className={styles.detailCard}>
                <h3 className={styles.detailTitle}>Histórico de tentativas</h3>
                <div className={styles.attemptList}>
                  {detail.attempts.map((attempt) => (
                    <article key={attempt.id} className={styles.attemptCard}>
                      <div className={styles.attemptHeader}>
                        <div>
                          <strong>Tentativa {attempt.attempt_number}</strong>
                          <div className={styles.helperText}>
                            {formatDateTime(attempt.requested_at)} →{' '}
                            {formatDateTime(attempt.finished_at)}
                          </div>
                        </div>
                        <span className={statusBadgeClass(attempt.status)}>
                          {statusLabel(attempt.status)}
                        </span>
                      </div>

                      <div className={styles.detailGrid}>
                        <div className={styles.detailCard}>
                          <h4 className={styles.detailTitle}>Metadados</h4>
                          <div className={styles.detailList}>
                            <div className={styles.detailRow}>
                              <span className={styles.detailKey}>HTTP status</span>
                              <span className={styles.detailValue}>
                                {attempt.response_status_code ?? '—'}
                              </span>
                            </div>
                            <div className={styles.detailRow}>
                              <span className={styles.detailKey}>Erro</span>
                              <span className={styles.detailValue}>
                                {attempt.error_message ?? '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className={styles.detailTitle}>Payload enviado</h4>
                        {attempt.request_payload_available ? (
                          <pre className={styles.jsonBlock}>{stringifyJson(attempt.request_payload)}</pre>
                        ) : (
                          <p className={styles.warningNote}>
                            Payload original indisponível para esta tentativa histórica.
                          </p>
                        )}
                      </div>

                      <div>
                        <h4 className={styles.detailTitle}>{downstreamResponseHeading}</h4>
                        <pre className={styles.jsonBlock}>{stringifyJson(attempt.response_payload)}</pre>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

interface MetaConversionsEventsPageProps {
  channel: MetaConversionsDashboardChannel
}

export function MetaConversionsEventsPage({ channel }: MetaConversionsEventsPageProps) {
  const { token } = useAuth()
  const {
    campaignId,
    campaignsLoading,
    hasCampaigns,
    isAdminWithoutInstitution,
  } = useMetaConversionsLayout()

  const defaultRange = useMemo(() => buildDefaultMetaDateRange(), [])
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom)
  const [dateTo, setDateTo] = useState(defaultRange.dateTo)
  const [status, setStatus] = useState<MetaConversionsEventStatus | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [events, setEvents] = useState<MetaConversionsEventListItem[]>([])
  const [pagination, setPagination] = useState<MetaConversionsPagination | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)

  useEffect(() => {
    setSelectedEventId(null)
  }, [channel])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(1)
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [campaignId, channel, dateFrom, dateTo, status])

  useEffect(() => {
    if (!campaignId) {
      setEvents([])
      setPagination(null)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        if (channel === 'meta') {
          const response = await fetchMetaConversionsEvents(
            campaignId,
            {
              page,
              perPage: PER_PAGE,
              dateFrom,
              dateTo,
              status,
              q: debouncedSearch || null,
            },
            token ?? undefined
          )

          if (!cancelled) {
            setEvents(response.events)
            setPagination(response.pagination)
          }
        } else {
          const response = await fetchGa4ConversionsEvents(
            campaignId,
            {
              page,
              perPage: PER_PAGE,
              dateFrom,
              dateTo,
              status,
              q: debouncedSearch || null,
            },
            token ?? undefined
          )

          if (!cancelled) {
            setEvents(response.events as MetaConversionsEventListItem[])
            setPagination(response.pagination as MetaConversionsPagination)
          }
        }
      } catch (err) {
        if (!cancelled) {
          setEvents([])
          setPagination(null)
          setError(
            err instanceof Error
              ? err.message
              : 'Não foi possível carregar os eventos de conversão.'
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [
    campaignId,
    page,
    dateFrom,
    dateTo,
    status,
    debouncedSearch,
    token,
    channel,
  ])

  if (isAdminWithoutInstitution && !campaignsLoading) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:office-building-outline" className={styles.stateIcon} />
        Selecione uma instituição no topo para visualizar os eventos.
      </div>
    )
  }

  if (!campaignsLoading && !hasCampaigns) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:bullhorn-outline" className={styles.stateIcon} />
        Nenhuma campanha cadastrada. Crie uma campanha para acompanhar os eventos.
      </div>
    )
  }

  if (campaignId == null) {
    return (
      <div className={styles.centeredState}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        Carregando campanha...
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.filtersCard}>
        <div className={styles.toolbarRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="meta-events-date-from">
              Data inicial
            </label>
            <input
              id="meta-events-date-from"
              type="date"
              className={styles.input}
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="meta-events-date-to">
              Data final
            </label>
            <input
              id="meta-events-date-to"
              type="date"
              className={styles.input}
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="meta-events-status">
              Status
            </label>
            <select
              id="meta-events-status"
              className={styles.selectNative}
              value={status}
              onChange={(event) => setStatus(event.target.value as MetaConversionsEventStatus | '')}
            >
              <option value="">Todos</option>
              <option value="sent">Enviado</option>
              <option value="failed">Falhou</option>
              <option value="skipped">Ignorado</option>
            </select>
          </div>
          <div className={styles.searchWrap}>
            <Icon icon="mdi:magnify" className={styles.searchIcon} />
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Buscar por event_id, pagamento, comprador ou email..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              aria-label={
                channel === 'meta'
                  ? 'Buscar eventos da Meta enviados via CAPI'
                  : 'Buscar eventos do GA4 enviados via Measurement Protocol'
              }
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.centeredState}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando eventos...
        </div>
      ) : error ? (
        <div className={styles.centeredState}>
          <Icon icon="mdi:alert-circle-outline" className={styles.stateIcon} />
          {error}
        </div>
      ) : (
        <>
          <div className={styles.tableCard}>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data/hora</th>
                    <th>Evento</th>
                    <th>Status</th>
                    <th>Pedido</th>
                    <th>Comprador</th>
                    <th>Valor</th>
                    <th>UTM source</th>
                    <th>UTM campaign</th>
                    <th>Origem do checkout</th>
                    <th>Tentativas</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={10} className={styles.emptyTable}>
                        Nenhum evento encontrado com os filtros atuais.
                      </td>
                    </tr>
                  ) : (
                    events.map((event) => (
                      <tr
                        key={event.id}
                        className={styles.tableRow}
                        tabIndex={0}
                        onClick={() => setSelectedEventId(event.id)}
                        onKeyDown={(keyboardEvent) => {
                          if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                            keyboardEvent.preventDefault()
                            setSelectedEventId(event.id)
                          }
                        }}
                      >
                        <td>{formatDateTime(event.event_time)}</td>
                        <td>{event.event_name}</td>
                        <td>
                          <span className={statusBadgeClass(event.status)}>
                            {statusLabel(event.status)}
                          </span>
                        </td>
                        <td>{event.payment_id ? `#${event.payment_id}` : '—'}</td>
                        <td>{event.donor_name ?? event.donor_email ?? '—'}</td>
                        <td>{event.amount != null ? formatCurrency(event.amount) : '—'}</td>
                        <td>{event.utm_source ?? '—'}</td>
                        <td>{event.utm_campaign ?? '—'}</td>
                        <td>{event.checkout_origin ?? '—'}</td>
                        <td>{formatNumber(event.attempts_count)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.footerRow}>
            {pagination && pagination.last_page > 1 ? (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.ghostButton}
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                >
                  Anterior
                </button>
                <span className={styles.paginationText}>
                  Página {pagination.current_page} de {pagination.last_page}
                </span>
                <button
                  type="button"
                  className={styles.ghostButton}
                  disabled={page >= pagination.last_page}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Próxima
                </button>
              </div>
            ) : (
              <span className={styles.paginationText}>
                {pagination ? `${formatNumber(pagination.total)} evento(s)` : '0 evento'}
              </span>
            )}
            <span className={styles.helperText}>
              Clique em uma linha para abrir o histórico completo do canal selecionado — payload, HTTP e
              tentativas.
            </span>
          </div>
        </>
      )}

      {selectedEventId !== null && typeof document !== 'undefined'
        ? createPortal(
            <EventDetailModal
              campaignId={campaignId}
              eventId={selectedEventId}
              channel={channel}
              onClose={() => setSelectedEventId(null)}
            />,
            document.body
          )
        : null}
    </div>
  )
}
